import {
  type FilterLegacy,
  FilterTypeLegacy,
  type InputFieldConfig,
  type InputFieldLegacy,
  InputFieldTypeLegacy,
  type InputOptionContextDataLegacy,
} from '@verantos-dev/types-cohorts';
import {
  Demographic_event_filterContext,
  Sequence_event_criterionContext,
  Condition_occurrence_event_filterContext,
  Drug_exposure_event_filterContext,
  Measurement_event_filterContext,
  Observation_event_filterContext,
  Procedure_occurrence_event_filterContext,
  Visit_occurrence_event_filterContext,
  Event_count_criteriaContext,
  Event_count_criteria_errorContext,
  Merged_event_filter_criterionContext,
  Measurement_numeric_filtersContext,
  Drug_exposure_numeric_filtersContext,
  Observation_numeric_filtersContext,
} from '@verantos-dev/util-cohort-builder-typescript-runtime';

import { TOKEN_OFFSET } from '../../constants/constants';
import { getCurrentCohortEventNames } from '../../utils/getCurrentCohortEventNames';
import { getNewFilterForType } from '../../utils/getNewFilterForType';

/**
 *
 * @param filters - the array of filters for the current variable or event
 * @param codeCompletionOptions - the codeCompletionOptions array
 * @param startIndex - the start location of the filters
 * @returns
 */
export const getNextFilters = (
  filterType: FilterTypeLegacy,
  filters: FilterLegacy[],
  filterContexts:
    | Demographic_event_filterContext[]
    | Sequence_event_criterionContext[]
    | Condition_occurrence_event_filterContext[]
    | Drug_exposure_event_filterContext[]
    | Measurement_event_filterContext[]
    | Observation_event_filterContext[]
    | Procedure_occurrence_event_filterContext[]
    | Visit_occurrence_event_filterContext[]
    | Event_count_criteriaContext[]
    | Event_count_criteria_errorContext[]
    | Merged_event_filter_criterionContext[],
  codeCompletionOptions: string[][],
  inputOptionContextData: InputOptionContextDataLegacy
) => {
  const nextInputFieldConfigMap: Record<string, InputFieldConfig> = {};
  const nextFilters: FilterLegacy[] = [];

  const { activeCohortBuilderItem, currentCohortDefinition } = inputOptionContextData;
  const eventNames = getCurrentCohortEventNames(activeCohortBuilderItem, currentCohortDefinition);
  filters.forEach((filter, i) => {
    const nextInputFields: InputFieldLegacy[] = [];
    const filterIndex = (filterContexts[i]?.start?.tokenIndex || 0) + TOKEN_OFFSET;

    /**
     * Generate a new input field if:
     * - no input fields exist
     * */
    if (filter.inputFields.length === 0) {
      const firstInputFieldOptions = codeCompletionOptions[filterIndex];
      const { newInputField, newInputFieldConfig } = getNewFilterForType(
        0,
        filterType,
        firstInputFieldOptions,
        {
          ...inputOptionContextData,
          eventNames,
        }
      );
      nextInputFieldConfigMap[newInputField.id] = newInputFieldConfig;
      nextInputFields.push({
        ...newInputField,
        value:
          [InputFieldTypeLegacy.COMBO_BOX, InputFieldTypeLegacy.DROPDOWN].includes(
            newInputFieldConfig.inputType
          ) && newInputFieldConfig.options?.length === 1
            ? newInputFieldConfig.options[0].value ?? ''
            : newInputField.value,
      });
    }

    /**
     * Update the input field options if:
     * - input fields exist
     */
    if (filter.inputFields.length > 0) {
      filter.inputFields.forEach((inputField, currentInputFieldIndex) => {
        const currentInputFieldOptions =
          codeCompletionOptions[filterIndex + currentInputFieldIndex];

        // Get the next input field config
        const { newInputFieldConfig } = getNewFilterForType(
          currentInputFieldIndex,
          filterType,
          currentInputFieldOptions,
          {
            ...inputOptionContextData,
            eventNames,
          }
        );
        nextInputFieldConfigMap[inputField.id] = newInputFieldConfig;

        // Get the next input field value
        let value: string = inputField.value ?? '';

        // If the input field is a combo box or dropdown
        if (
          [InputFieldTypeLegacy.COMBO_BOX, InputFieldTypeLegacy.DROPDOWN].includes(
            newInputFieldConfig.inputType
          )
        ) {
          // has only one option, set the value to the option value
          if (newInputFieldConfig.options?.length === 1) {
            value = newInputFieldConfig.options[0].value ?? inputField.value ?? '';
          }

          // clear the input value if no option matches current value selection
          if (
            !newInputFieldConfig.options?.some((option) => {
              return option.value === inputField.value;
            })
          ) {
            value = '';
          }
        }

        nextInputFields.push({
          ...inputField,
          value,
        });
      });
    }

    /*
     * This is for grabbing the numeric filters, witch could contain floats.  This is not all numeric rule contexts.
     * We grab these to help determine the end state for float-based filters to avoid generating an additional input field.
     * This is specifically used for rules in simple events.
     *
     * The filterContexts[i] is the parent context for the numeric filters. The children of the filterContexts[i]
     * will be an error_node until the values are filled out in full to define a numeric rule.
     * At this point the children will contain this numeric rule context and we can check for it with the getRuleContexts method.
     */
    const numericRuleContexts = [
      ...filterContexts[i].getRuleContexts(Measurement_numeric_filtersContext),
      ...filterContexts[i].getRuleContexts(Drug_exposure_numeric_filtersContext),
      ...filterContexts[i].getRuleContexts(Observation_numeric_filtersContext),
    ];

    /**
     * Add a new input field if all are true:
     * - the last input field has a value
     * - the next input field has options
     * - the previous input field is not a CONCEPT_GROUP
     * - the next input field is not a closing parenthesis & closing parenthesis is the only option
     * - we are not in a float-based, numeric rule context
     **/
    if (
      // skip adding if last value is empty
      nextInputFields[nextInputFields.length - 1].value !== '' &&
      // skip adding if next input field has no options
      codeCompletionOptions[filterIndex + filter.inputFields.length]?.length > 0 &&
      // skip adding if previous input field is a CONCEPT_GROUP
      !nextInputFields[nextInputFields.length - 1].value?.startsWith('CONCEPT_GROUP') &&
      // skip adding if next input field is a closing parenthesis & closing parenthesis is the only option
      !(
        codeCompletionOptions[filterIndex + filter.inputFields.length].length === 1 &&
        codeCompletionOptions[filterIndex + filter.inputFields.length][0] === "')'"
      ) &&
      // skip adding if the we are in a numeric rule context, all input fields are manually added upfront
      !(numericRuleContexts.length > 0)
    ) {
      /*
       * This is to prevent the generation of an additional input field if the last input field
       * is a float. if a numeric rule context is found, in a simple event.
       */
      const { newInputField, newInputFieldConfig } = getNewFilterForType(
        nextInputFields.length,
        filterType,
        codeCompletionOptions[filterIndex + nextInputFields.length],
        {
          ...inputOptionContextData,
          eventNames,
        }
      );
      nextInputFieldConfigMap[newInputField.id] = newInputFieldConfig;

      nextInputFields.push({
        ...newInputField,
        value:
          [InputFieldTypeLegacy.COMBO_BOX, InputFieldTypeLegacy.DROPDOWN].includes(
            newInputFieldConfig.inputType
          ) && newInputFieldConfig.options?.length === 1
            ? newInputFieldConfig.options[0].value ?? ''
            : newInputField.value,
      });
    }

    nextFilters.push({
      ...filter,
      inputFields: nextInputFields,
    });
  });

  return {
    nextInputFieldConfigMap,
    nextFilters,
  };
};
